/**
 * Central registry of every calculator screen route.
 * Used to compute coverage ("X of Y calculators used") and to
 * validate favorites / recent entries against known calcs.
 *
 * Keep in sync with the Stack.Screen entries in App.tsx.
 */

import type { Slot } from './slots';

export type CalcCategory = 'TAB' | 'Cleanroom' | 'Fumehood' | 'BET';

export type CalcEntry = {
  /** Navigator (top-level tab) */
  category: CalcCategory;
  /** Stack screen name within that navigator */
  route: string;
  /** Human-readable title */
  title: string;
  /** Value-bus slots this calc consumes when opened (for prefill). */
  inputs?: Slot[];
  /** Value-bus slots this calc can produce on a successful calculation. */
  outputs?: Slot[];
};

export const CALC_REGISTRY: CalcEntry[] = [
  // ══════════════════════════════════════════════════════════════════════
  //  TAB — Airflow
  // ══════════════════════════════════════════════════════════════════════
  {
    category: 'TAB', route: 'AirflowVolumetricFlowRate', title: 'Volumetric Flow Rate',
    inputs:  ['velocity_fpm', 'area_sqft', 'cfm'],
    outputs: ['velocity_fpm', 'area_sqft', 'cfm'],
  },
  {
    category: 'TAB', route: 'AirflowTotalPressure', title: 'Total Pressure',
    inputs:  ['static_pressure_iwg', 'velocity_pressure_iwg', 'total_pressure_iwg'],
    outputs: ['static_pressure_iwg', 'velocity_pressure_iwg', 'total_pressure_iwg'],
  },
  {
    category: 'TAB', route: 'AirflowVelocityOfAir', title: 'Velocity of Air',
    inputs:  ['velocity_pressure_iwg', 'velocity_fpm', 'air_density_lb_ft3'],
    outputs: ['velocity_pressure_iwg', 'velocity_fpm', 'air_density_lb_ft3'],
  },
  {
    category: 'TAB', route: 'AirflowAirChanges', title: 'Air Changes',
    inputs:  ['cfm', 'volume_cuft', 'air_changes_per_hour'],
    outputs: ['cfm', 'volume_cuft', 'air_changes_per_hour'],
  },
  {
    category: 'TAB', route: 'AirflowDuctArea', title: 'Duct Area',
    inputs:  ['duct_diameter_in', 'duct_width_in', 'duct_height_in', 'area_sqft'],
    outputs: ['area_sqft', 'duct_diameter_in', 'duct_width_in', 'duct_height_in'],
  },
  {
    category: 'TAB', route: 'AirflowHoodFaceVelocity', title: 'Hood Face Velocity',
    inputs:  ['velocity_fpm', 'area_sqft', 'cfm'],
    outputs: ['velocity_fpm', 'area_sqft', 'cfm'],
  },
  {
    category: 'TAB', route: 'AirflowMixedAirTemperature', title: 'Mixed Air Temperature',
    inputs:  ['outside_air_temp_f', 'return_air_temp_f', 'outside_air_pct', 'mixed_air_temp_f'],
    outputs: ['outside_air_temp_f', 'return_air_temp_f', 'outside_air_pct', 'mixed_air_temp_f'],
  },
  {
    category: 'TAB', route: 'AirflowPitotTraverseAveraging', title: 'Pitot Traverse Averaging',
    inputs:  ['air_density_lb_ft3'],
    outputs: ['velocity_fpm', 'velocity_pressure_iwg'],
  },
  {
    category: 'TAB', route: 'AirflowFlowHoodKFactor', title: 'Flow Hood K-Factor',
    inputs:  ['velocity_pressure_iwg', 'cfm', 'k_factor'],
    outputs: ['velocity_pressure_iwg', 'cfm', 'k_factor'],
  },
  {
    category: 'TAB', route: 'AirflowDuctLeakageClass', title: 'Duct Leakage',
    inputs:  ['static_pressure_iwg', 'leakage_cfm_per_100sqft'],
    outputs: ['leakage_cfm_per_100sqft'],
  },
  {
    category: 'TAB', route: 'AirflowDuctSizeFromCFM', title: 'Duct Size from CFM',
    inputs:  ['cfm', 'velocity_fpm', 'duct_width_in'],
    outputs: ['area_sqft', 'duct_diameter_in', 'duct_height_in', 'duct_width_in'],
  },
  {
    category: 'TAB', route: 'AirflowEquivalentRoundDuct', title: 'Equivalent Round Duct',
    inputs:  ['duct_width_in', 'duct_height_in'],
    outputs: ['duct_diameter_in'],
  },
  {
    category: 'TAB', route: 'AirflowPitotTraverseGrid', title: 'Pitot Traverse Grid',
    inputs:  ['area_sqft', 'air_density_lb_ft3'],
    outputs: ['velocity_fpm', 'velocity_pressure_iwg', 'cfm'],
  },

  // ── Fan / Sheave ──────────────────────────────────────────────────────
  {
    category: 'TAB', route: 'FanEquationsFanAffinityLaws', title: 'Fan Affinity Laws',
    inputs:  ['fan_rpm', 'cfm', 'static_pressure_iwg', 'bhp'],
    outputs: ['fan_rpm', 'cfm', 'static_pressure_iwg', 'bhp'],
  },
  {
    category: 'TAB', route: 'FanEquationsTipSpeed', title: 'Tip Speed',
    inputs:  ['fan_rpm', 'fan_pd_in'],
    outputs: ['tip_speed_fpm'],
  },
  {
    category: 'TAB', route: 'SheaveEquationsRPMandPD', title: 'RPM and PD',
    inputs:  ['fan_rpm', 'motor_rpm', 'fan_pd_in', 'motor_pd_in'],
    outputs: ['fan_rpm', 'motor_rpm', 'fan_pd_in', 'motor_pd_in'],
  },
  {
    category: 'TAB', route: 'SheaveEquationsFanBeltLength', title: 'Fan Belt Length',
    inputs:  ['fan_pd_in', 'motor_pd_in', 'center_distance_in'],
    outputs: ['belt_length_in'],
  },

  // ── Hydronic ──────────────────────────────────────────────────────────
  {
    category: 'TAB', route: 'HydronicPumpLawsWaterHorsepower', title: 'Water Horsepower',
    inputs:  ['gpm', 'pump_head_ft', 'specific_gravity'],
    outputs: ['water_hp'],
  },
  {
    category: 'TAB', route: 'HydronicPumpLawsBrakeHorsepower', title: 'Brake Horsepower',
    inputs:  ['water_hp', 'pump_efficiency_pct', 'gpm', 'pump_head_ft'],
    outputs: ['bhp'],
  },
  {
    category: 'TAB', route: 'HydronicPumpEfficiency', title: 'Pump Efficiency',
    inputs:  ['water_hp', 'bhp'],
    outputs: ['pump_efficiency_pct'],
  },
  {
    category: 'TAB', route: 'HydronicDeltaPCoilDeltaP', title: 'Coil Delta P',
    inputs:  ['gpm', 'cv', 'specific_gravity', 'delta_p_psi'],
    outputs: ['delta_p_psi'],
  },
  {
    category: 'TAB', route: 'HydronicDeltaPCvandGPM', title: 'Cv and GPM',
    inputs:  ['gpm', 'cv', 'delta_p_psi', 'specific_gravity'],
    outputs: ['gpm', 'cv', 'delta_p_psi'],
  },
  {
    category: 'TAB', route: 'HydronicHeatTransferCalculations', title: 'Heat Transfer Calculations',
    inputs:  ['gpm', 'delta_t_f', 'q_btuh'],
    outputs: ['gpm', 'delta_t_f', 'q_btuh'],
  },
  {
    category: 'TAB', route: 'HydronicBoilerOutput', title: 'Boiler Output',
    inputs:  ['boiler_output_btuh'],
    outputs: ['boiler_output_btuh'],
  },
  {
    category: 'TAB', route: 'HydronicBoilerFireRate', title: 'Fire Rate',
    inputs:  ['boiler_output_btuh'],
    outputs: ['boiler_fire_rate_gph'],
  },
  { category: 'TAB', route: 'HydronicBoilerOperatingCost', title: 'Operating Cost',
    inputs: ['boiler_fire_rate_gph'],
  },

  // ── Electrical ────────────────────────────────────────────────────────
  {
    category: 'TAB', route: 'ElectricalVoltsAmpsOhms', title: 'Volts/Amps/Ohms',
    inputs:  ['volts', 'amps', 'resistance_ohms'],
    outputs: ['volts', 'amps', 'resistance_ohms'],
  },
  {
    category: 'TAB', route: 'ElectricalPowerPowerCalculations', title: 'Power Calculations',
    inputs:  ['volts', 'amps', 'power_factor', 'power_w'],
    outputs: ['volts', 'amps', 'power_factor', 'power_w'],
  },
  {
    category: 'TAB', route: 'ElectricalMotorFLA', title: 'Motor FLA',
    inputs:  ['volts', 'power_factor', 'motor_efficiency_pct', 'bhp'],
    outputs: ['motor_fla', 'amps'],
  },
  {
    category: 'TAB', route: 'ElectricalMotorCalculationsFanHorsepower', title: 'Fan Horsepower',
    inputs:  ['cfm', 'static_pressure_iwg', 'fan_efficiency_pct'],
    outputs: ['fan_hp'],
  },
  {
    category: 'TAB', route: 'ElectricalMotorCalculationsBrakeHorsepower', title: 'Motor Brake Horsepower',
    inputs:  ['volts', 'amps', 'power_factor', 'motor_efficiency_pct'],
    outputs: ['bhp'],
  },
  {
    category: 'TAB', route: 'ElectricalElectricalSystemsTotalResistance', title: 'Total Resistance',
    inputs:  ['resistance_ohms'],
    outputs: ['resistance_ohms'],
  },
  { category: 'TAB', route: 'ElectricalElectricalSystemsPrimarySecondary', title: 'Primary/Secondary',
    inputs: ['volts', 'amps'], outputs: ['volts', 'amps'],
  },
  {
    category: 'TAB', route: 'ElectricalElectricalSystemsVoltageDrop', title: 'Voltage Drop',
    inputs:  ['amps', 'volts'],
    outputs: ['volts'],
  },

  // ── Air Temperature & Psychrometrics ──────────────────────────────────
  {
    category: 'TAB', route: 'AirTemperatureOutsideAirPercentage', title: 'Outside Air Percentage',
    inputs:  ['outside_air_temp_f', 'return_air_temp_f', 'mixed_air_temp_f', 'outside_air_pct'],
    outputs: ['outside_air_pct', 'mixed_air_temp_f'],
  },
  {
    category: 'TAB', route: 'AirTemperatureMixedAirEnthalpy', title: 'Mixed Air Enthalpy',
    inputs:  ['outside_air_pct', 'enthalpy_btu_lb'],
    outputs: ['enthalpy_btu_lb'],
  },
  {
    category: 'TAB', route: 'PsychrometricsDewPointEnthalpy', title: 'Psychrometrics',
    inputs:  ['dry_bulb_f', 'rh_pct', 'wet_bulb_f'],
    outputs: ['dry_bulb_f', 'wet_bulb_f', 'dewpoint_f', 'rh_pct', 'humidity_ratio_lb_lb', 'enthalpy_btu_lb', 'specific_volume_ft3_lb'],
  },
  {
    category: 'TAB', route: 'PsychrometricsMixedAir', title: 'Mixed Air (Psychrometrics)',
    inputs:  ['outside_air_temp_f', 'return_air_temp_f', 'outside_air_pct', 'rh_pct'],
    outputs: ['mixed_air_temp_f', 'enthalpy_btu_lb', 'humidity_ratio_lb_lb'],
  },

  // ── Heat Transfer ─────────────────────────────────────────────────────
  {
    category: 'TAB', route: 'HeatTransferHeatTransferTotal', title: 'Total Heat Transfer',
    inputs:  ['sensible_heat_btuh', 'latent_heat_btuh', 'q_btuh'],
    outputs: ['q_btuh'],
  },
  {
    category: 'TAB', route: 'HeatTransferSensibleHeatTransfer', title: 'Sensible Heat Transfer',
    inputs:  ['cfm', 'delta_t_f', 'sensible_heat_btuh'],
    outputs: ['cfm', 'delta_t_f', 'sensible_heat_btuh'],
  },
  {
    category: 'TAB', route: 'HeatTransferLatentHeatTransfer', title: 'Latent Heat Transfer',
    inputs:  ['cfm', 'humidity_ratio_lb_lb', 'latent_heat_btuh'],
    outputs: ['cfm', 'humidity_ratio_lb_lb', 'latent_heat_btuh'],
  },
  {
    category: 'TAB', route: 'HeatTransferSensibleHeatRatio', title: 'Sensible Heat Ratio',
    inputs:  ['sensible_heat_btuh', 'q_btuh', 'latent_heat_btuh'],
    outputs: ['shr'],
  },
  {
    category: 'TAB', route: 'HeatTransferQBtuh', title: 'Q BTU/h',
    inputs:  ['cfm', 'delta_t_f', 'q_btuh'],
    outputs: ['cfm', 'delta_t_f', 'q_btuh'],
  },

  // ── Duct loss / misc ──────────────────────────────────────────────────
  {
    category: 'TAB', route: 'DuctFrictionLoss', title: 'Duct Friction Loss',
    inputs:  ['cfm', 'duct_diameter_in', 'duct_width_in', 'duct_height_in', 'duct_length_ft'],
    outputs: ['friction_loss_iwg_per_100ft', 'static_pressure_iwg', 'velocity_fpm'],
  },
  {
    category: 'TAB', route: 'DuctFittingLossEquivalentLength', title: 'Fitting Loss & Equivalent Length',
    inputs:  ['velocity_pressure_iwg', 'velocity_fpm', 'duct_diameter_in', 'friction_loss_iwg_per_100ft'],
    outputs: ['equivalent_length_ft', 'static_pressure_iwg'],
  },
  { category: 'TAB', route: 'UnitConversions', title: 'Unit Conversions' },
  {
    category: 'TAB', route: 'FilterPressureDrop', title: 'Filter Pressure Drop',
    inputs:  ['velocity_fpm', 'cfm', 'area_sqft'],
    outputs: ['filter_pressure_drop_iwg', 'static_pressure_iwg'],
  },

  // ══════════════════════════════════════════════════════════════════════
  //  Cleanroom
  // ══════════════════════════════════════════════════════════════════════
  {
    category: 'Cleanroom', route: 'CleanroomAirChangesPerHour', title: 'Air Changes Per Hour',
    inputs:  ['cfm', 'volume_cuft', 'air_changes_per_hour'],
    outputs: ['cfm', 'volume_cuft', 'air_changes_per_hour'],
  },
  {
    category: 'Cleanroom', route: 'CleanroomParticleConcentration', title: 'Particle Concentration',
    inputs:  ['sample_volume_ft3', 'particle_concentration_per_ft3'],
    outputs: ['particle_concentration_per_ft3'],
  },
  {
    category: 'Cleanroom', route: 'CleanroomScanRate', title: 'HEPA Scan Rate',
    inputs:  ['scan_rate_fpm'],
    outputs: ['scan_rate_fpm'],
  },
  {
    category: 'Cleanroom', route: 'CleanroomParticleCountCalculator', title: 'ISO 14644-1 Particle Count',
    inputs:  ['floor_area_sqft', 'iso_class'],
  },

  // ══════════════════════════════════════════════════════════════════════
  //  Fumehood
  // ══════════════════════════════════════════════════════════════════════
  {
    category: 'Fumehood', route: 'FumehoodFaceVelocity', title: 'Face Velocity & CFM',
    inputs:  ['velocity_fpm', 'area_sqft', 'cfm'],
    outputs: ['velocity_fpm', 'area_sqft', 'cfm'],
  },
  {
    category: 'Fumehood', route: 'FumehoodMultiPointAverage', title: 'Multi-Point Average',
    inputs:  ['area_sqft'],
    outputs: ['velocity_fpm', 'cfm'],
  },
  {
    category: 'Fumehood', route: 'FumehoodFaceArea', title: 'Face Area',
    inputs:  ['duct_width_in', 'duct_height_in', 'area_sqft'],
    outputs: ['area_sqft'],
  },
  {
    category: 'Fumehood', route: 'FumehoodPressureDifferential', title: 'Pressure Differential',
    inputs:  ['pressure_differential_pa', 'pressure_differential_iwg'],
    outputs: ['pressure_differential_pa', 'pressure_differential_iwg'],
  },

  // ══════════════════════════════════════════════════════════════════════
  //  BET — Building Envelope Testing
  // ══════════════════════════════════════════════════════════════════════
  {
    category: 'BET', route: 'BETPressurization', title: 'ASTM E779 Pressurization',
    inputs:  ['volume_cuft'],
    outputs: ['cfm', 'air_changes_per_hour', 'effective_leakage_area_sqin'],
  },
  {
    category: 'BET', route: 'BETDepressurization', title: 'ASTM E1827 Depressurization',
    inputs:  ['volume_cuft'],
    outputs: ['cfm', 'air_changes_per_hour', 'effective_leakage_area_sqin'],
  },
  {
    category: 'BET', route: 'BETRegression', title: 'Multi-Point Regression',
    outputs: ['cfm'],
  },
  {
    category: 'BET', route: 'BETAirLeakageRate', title: 'Air Leakage Rate',
    inputs:  ['cfm', 'volume_cuft'],
    outputs: ['cfm', 'air_changes_per_hour'],
  },
  {
    category: 'BET', route: 'BETEffectiveLeakageArea', title: 'Effective Leakage Area',
    inputs:  ['cfm', 'pressure_differential_pa'],
    outputs: ['effective_leakage_area_sqin'],
  },
  {
    category: 'BET', route: 'BETAirChangesPerHour', title: 'Air Changes Per Hour',
    inputs:  ['cfm', 'volume_cuft', 'air_changes_per_hour'],
    outputs: ['cfm', 'volume_cuft', 'air_changes_per_hour'],
  },
];

export const CALC_ROUTE_SET: Set<string> = new Set(CALC_REGISTRY.map((c) => c.route));

export function findCalc(route: string): CalcEntry | undefined {
  return CALC_REGISTRY.find((c) => c.route === route);
}

export function calcsByCategory(category: CalcCategory): CalcEntry[] {
  return CALC_REGISTRY.filter((c) => c.category === category);
}

/**
 * Downstream calcs for a given route — any other calc whose declared
 * inputs overlap this route's declared outputs. Used to render the
 * "Use in →" chip row below a successful result.
 */
export function downstreamCalcs(fromRoute: string): CalcEntry[] {
  const src = findCalc(fromRoute);
  if (!src?.outputs?.length) return [];
  const outSet = new Set<Slot>(src.outputs);
  return CALC_REGISTRY.filter((c) => {
    if (c.route === fromRoute) return false;
    if (!c.inputs?.length) return false;
    return c.inputs.some((s) => outSet.has(s));
  });
}

export const CATEGORY_ORDER: CalcCategory[] = ['TAB', 'Cleanroom', 'Fumehood', 'BET'];

export const CATEGORY_META: Record<CalcCategory, { label: string; color: string; icon: string }> = {
  TAB: { label: 'Testing, Adjusting & Balancing', color: '#4CAF50', icon: 'clipboard' },
  Cleanroom: { label: 'Cleanroom Testing', color: '#2196F3', icon: 'shield-checkmark' },
  Fumehood: { label: 'Fume Hood Testing', color: '#FF9800', icon: 'flask' },
  BET: { label: 'Building Envelope Testing', color: '#9C27B0', icon: 'business-outline' },
};
